import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Typography from "@mui/material/Typography"

import { useState } from "react"

import FileUploaderV2 from "components/FileUploaderV2"
import { ProjectFormPageProps } from "components/ProjectForm"
import EModuleIds, { ESubModuleIds } from "enums/ModuleEnum"
import ProjectFormFooter from "../../components/ProjectFormFooter"
import DemolitionChargersAndMaterials from "./Demolition"
import SkusListModal from "./SkusListModal"
import useChargersAndMaterials, { EChargersAndMaterialsFields } from "./useChargersAndMaterials"

export const tableHeaderCellStyles = {
    color: "#70758D",
    fontSize: "11px",
    padding: "4px 8px",
    border: "1px solid #E5E7EB",
    fontWeight: 600,
    backgroundColor: "#FAFAFA",
}

export const tableCellStyles = {
    color: "#101941",
    fontSize: "12px",
    padding: "0",
    border: "1px solid #E5E7EB",
    height: "36px",
}

const ChargersAndMaterialsDefault = (props: ProjectFormPageProps) => {
    const { states, functions } = useChargersAndMaterials(props)
    const [skusModal, setSkusModal] = useState<"charger" | "material" | null>(null)
    return (
        <>
            <Box
                display={"flex"}
                flexDirection={"column"}
                gap={4}
                className="custom-scrollbar"
                maxHeight={"65vh"}
                padding={"var(--mui-modal-padding)"}
                sx={{
                    overflowY: "auto",
                }}>
                <Box display={"flex"} flexDirection={{ xs: "column", sm: "row" }} gap={2}>
                    <Box display={"flex"} flexDirection={"column"} gap={2} flex={1}>
                        <Typography fontSize={"14px"} variant="label">
                            Charger Details*
                        </Typography>
                        <FileUploaderV2
                            allowDrop={true}
                            accept={".xlsx, .xls, .xlsm"}
                            onChange={files => functions.handleParseChargersFile(files)}
                        />
                        <Button
                            variant="outlined"
                            onClick={() => functions.handleDownloadTemplate("charger")}
                            loading={states.isFetchingChargerTemplate}>
                            Download Excel Template
                        </Button>
                    </Box>
                    <Box display={"flex"} flexDirection={"column"} gap={2} flex={1}>
                        <Typography fontSize={"14px"} variant="label">
                            Material Details*
                        </Typography>
                        <FileUploaderV2
                            allowDrop={true}
                            accept={".xlsx, .xls, .xlsm"}
                            onChange={files => functions.handleParseMaterialsFile(files)}
                        />
                        <Button
                            variant="outlined"
                            onClick={() => functions.handleDownloadTemplate("material")}
                            loading={states.isFetchingMaterialTemplate}>
                            Download Excel Template
                        </Button>
                    </Box>
                </Box>
                <Box display={"flex"} flexDirection={"column"} gap={2}>
                    <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
                        <Box display={"flex"} alignItems={"center"} gap={2}>
                            <Typography fontSize={"14px"} variant="label" color={"statiqOrange.main"}>
                                CHARGER{" "}
                                {states.formik.values[EChargersAndMaterialsFields.CHARGERS].length > 0 &&
                                    `(${states.formik.values[EChargersAndMaterialsFields.CHARGERS].length.toString().padStart(2, "0")})`}
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                sx={{ fontSize: "10px", padding: "2px 8px", minWidth: "auto" }}
                                onClick={() => setSkusModal("charger")}>
                                View Items
                            </Button>
                        </Box>
                        <Typography
                            fontSize={"10px"}
                            onClick={functions.handleShowMoreChargers}
                            variant="label"
                            fontWeight={"300"}
                            textTransform={"none"}
                            sx={{
                                textDecoration: "underline",
                                textUnderlineOffset: "2px",
                                cursor: "pointer",
                            }}>
                            See {states.showMoreChargers ? "Less" : "More"}
                        </Typography>
                    </Box>
                    {states.showMoreChargers && (
                        <Box display={"flex"} flexDirection={"column"} gap={2}>
                            <TableContainer className="custom-scrollbar" sx={{ maxHeight: "300px", overflowY: "auto" }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            {states.chargerColumns.map((column, index) => (
                                                <TableCell
                                                    key={`charger-header-${index}`}
                                                    sx={{ ...tableHeaderCellStyles, width: column.width }}>
                                                    {column.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {states.formik.values[EChargersAndMaterialsFields.CHARGERS].length === 0 ? (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={states.chargerColumns.length}
                                                    sx={{
                                                        ...tableCellStyles,
                                                        textAlign: "center",
                                                        color: "#70758D",
                                                        padding: "12px",
                                                    }}>
                                                    No chargers added
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            states.formik.values[EChargersAndMaterialsFields.CHARGERS].map(
                                                (row, rowIndex) => (
                                                    <TableRow key={`charger-row-${rowIndex}`}>
                                                        {states.chargerColumns.map((column, colIndex) => {
                                                            let cellContent
                                                            if (column.id === "index") {
                                                                cellContent = rowIndex + 1
                                                            } else if (column.id === "custom") {
                                                                cellContent = column.render?.(
                                                                    undefined as any,
                                                                    row,
                                                                    rowIndex
                                                                )
                                                            } else {
                                                                const value = (row as any)[column.id]
                                                                cellContent = column.render
                                                                    ? column.render(value, row, rowIndex)
                                                                    : value || "-"
                                                            }
                                                            return (
                                                                <TableCell
                                                                    key={`charger-cell-${rowIndex}-${colIndex}`}
                                                                    sx={{
                                                                        ...tableCellStyles,
                                                                        width: column.width,
                                                                        ...(column.id === "index"
                                                                            ? { padding: "4px 8px" }
                                                                            : {}),
                                                                    }}>
                                                                    {cellContent}
                                                                </TableCell>
                                                            )
                                                        })}
                                                    </TableRow>
                                                )
                                            )
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Box display="flex" justifyContent="center">
                                <Button variant="outlined" color="primary" onClick={functions.handleAddChargerRow}>
                                    + Add New Charger
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
                <Box display={"flex"} flexDirection={"column"} gap={2}>
                    <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
                        <Box display={"flex"} alignItems={"center"} gap={2}>
                            <Typography fontSize={"14px"} variant="label" color={"statiqOrange.main"}>
                                MATERIAL{" "}
                                {states.formik.values[EChargersAndMaterialsFields.MATERIALS].length > 0 &&
                                    `(${states.formik.values[EChargersAndMaterialsFields.MATERIALS].length.toString().padStart(2, "0")})`}
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                sx={{ fontSize: "10px", padding: "2px 8px", minWidth: "auto" }}
                                onClick={() => setSkusModal("material")}>
                                View Items
                            </Button>
                        </Box>

                        <Typography
                            fontSize={"10px"}
                            onClick={functions.handleShowMoreMaterials}
                            variant="label"
                            fontWeight={"300"}
                            textTransform={"none"}
                            sx={{
                                textDecoration: "underline",
                                textUnderlineOffset: "2px",
                                cursor: "pointer",
                            }}>
                            See {states.showMoreMaterials ? "Less" : "More"}
                        </Typography>
                    </Box>
                    {states.showMoreMaterials && (
                        <Box display={"flex"} flexDirection={"column"} gap={2}>
                            <TableContainer className="custom-scrollbar" sx={{ maxHeight: "300px", overflowY: "auto" }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            {states.materialColumns.map((column, index) => (
                                                <TableCell
                                                    key={`material-header-${index}`}
                                                    sx={{ ...tableHeaderCellStyles, width: column.width }}>
                                                    {column.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {states.formik.values[EChargersAndMaterialsFields.MATERIALS].length === 0 ? (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={states.materialColumns.length}
                                                    sx={{
                                                        ...tableCellStyles,
                                                        textAlign: "center",
                                                        color: "#70758D",
                                                        padding: "12px",
                                                    }}>
                                                    No materials added
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            states.formik.values[EChargersAndMaterialsFields.MATERIALS].map(
                                                (row, rowIndex) => (
                                                    <TableRow key={`material-row-${rowIndex}`}>
                                                        {states.materialColumns.map((column, colIndex) => {
                                                            let cellContent
                                                            if (column.id === "index") {
                                                                cellContent = rowIndex + 1
                                                            } else if (column.id === "custom") {
                                                                cellContent = column.render?.(
                                                                    undefined as any,
                                                                    row,
                                                                    rowIndex
                                                                )
                                                            } else {
                                                                const value = (row as any)[column.id]
                                                                cellContent = column.render
                                                                    ? column.render(value, row, rowIndex)
                                                                    : value || "-"
                                                            }
                                                            return (
                                                                <TableCell
                                                                    key={`material-cell-${rowIndex}-${colIndex}`}
                                                                    sx={{
                                                                        ...tableCellStyles,
                                                                        width: column.width,
                                                                        ...(column.id === "index"
                                                                            ? { padding: "4px 8px" }
                                                                            : {}),
                                                                    }}>
                                                                    {cellContent}
                                                                </TableCell>
                                                            )
                                                        })}
                                                    </TableRow>
                                                )
                                            )
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Box display="flex" justifyContent="center">
                                <Button variant="outlined" color="primary" onClick={functions.handleAddMaterialRow}>
                                    + Add New Material
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>
            <ProjectFormFooter
                handleCloseForm={props.handleCloseForm}
                isLoading={states.isUpdatingPageData || states.isRaisingLogisticsTicket}
                submitButtonText={states.isLastPage ? "Submit" : "Next"}
                handleSubmit={() => states.formik.handleSubmit()}
            />
            {skusModal && <SkusListModal open={!!skusModal} onClose={() => setSkusModal(null)} type={skusModal} />}
        </>
    )
}

const ChargersAndMaterials = (props: ProjectFormPageProps) => {
    if (props.moduleId === EModuleIds.OPERATIONS && props.subModuleId === ESubModuleIds.DEMOLITION) {
        return <DemolitionChargersAndMaterials {...props} />
    }
    return <ChargersAndMaterialsDefault {...props} />
}

export default ChargersAndMaterials
